import { ApiError, apiFetch } from "../api/client";
import { readLanguage } from "../settings/langue";
import { normalizeEmail } from "./otp";
import { openSession } from "./session";

/**
 * ═══ L'ADRESSE, AVANT TOUT LE RESTE — LE PRINCIPE DE LA V1 ═══
 *
 * L'écran de connexion de la V1 (`screens/Login/index.jsx`) ne demande pas un mot de
 * passe à une adresse qu'il ne connaît pas : il VÉRIFIE l'adresse d'abord, et c'est la
 * réponse qui décide de la suite — mot de passe (ou lien) si elle est connue, création
 * du compte si elle ne l'est pas et que l'organisateur l'autorise, refus sinon. Ce
 * module porte ces deux appels, sur api-v1 comme la V1 :
 *
 *   GET  /v2/domains/:domainId/users/check?email=…   → `true` si un compte du DOMAINE
 *        porte cette adresse (`routesV2/users/checkEmail.js`). Public.
 *   POST /domains/:domainId/users?eventId=…          → crée le compte (et son
 *        `Authentication`), pose la typologie sur l'inscription si elle est envoyée,
 *        et rend un JETON : la personne est connectée sans autre geste
 *        (`controllers/User.controller.js`, `create`). Public aussi
 *        (`isPrivateAndPublic`).
 *
 * ⚠️ CE QUE CELA RÉVÈLE, ET QUE LA V2 TAISAIT. `signIn` prend soin de répondre la même
 * chose à « adresse inconnue » et à « mauvais mot de passe », pour ne pas faire de
 * l'écran un annuaire. Vérifier l'adresse d'abord dit, par construction, si elle est
 * connue. C'est le principe de la V1, demandé tel quel (2026-09-16) : sans lui, on ne
 * peut ni proposer la création à qui n'a pas de compte, ni le refuser proprement.
 *
 * ⚠️ PAS DE MOT DE PASSE À LA CRÉATION, comme la V1 : son formulaire de création n'en
 * a pas (prénom, nom, typologie au plus), le compte naît sans, et le jeton rendu ouvre
 * la session. Quelqu'un qui revient plus tard passe par « mot de passe oublié », le
 * lien ou le code, selon le mode de l'événement.
 */

/**
 * La forme d'une adresse, jugée AVANT d'appeler le serveur : quelque chose, un `@`,
 * quelque chose, un point, quelque chose. Le formulaire est en `noValidate` (ses
 * autres messages sont les siens), donc le navigateur ne le dit pas à notre place.
 */
export function isEmailShape(raw: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());
}

export async function isKnownEmail(
  domainId: number,
  email: string,
): Promise<boolean> {
  const known = await apiFetch<unknown>(
    `/v2/domains/${domainId}/users/check?email=${encodeURIComponent(normalizeEmail(email))}`,
  );
  return known === true;
}

export type NewAccount = {
  email: string;
  firstName: string;
  lastName: string;
  /** La typologie choisie à la création, quand l'événement la demande. */
  typoId?: number | null;
};

/** Ce que le serveur dit d'une création refusée, ramené à ce que l'écran peut en faire. */
export type SignUpRefusal =
  /** L'adresse a un compte, finalement : on revient à la saisie et on le dit. */
  | "taken"
  /** Un émoji dans le prénom ou le nom (#508) : on le dit, l'adresse reste libre. */
  | "emoji"
  /** Le service ne répond pas comme prévu : ce n'est pas la faute de la personne. */
  | "unavailable";

export class SignUpError extends Error {
  constructor(readonly refusal: SignUpRefusal) {
    super(refusal);
    this.name = "SignUpError";
  }
}

/** Le message exact du service (`User.service.js`) quand l'adresse existe déjà. */
const TAKEN = /déjà utilisé/i;

export function signUpRefusal(cause: unknown): SignUpError {
  if (cause instanceof ApiError && TAKEN.test(cause.message))
    return new SignUpError("taken");
  /* Le refus d'api-v1 (`helpers/fourByteChars`), avant toute écriture. L'écran le
     dit avant d'envoyer ; ceci couvre ce qu'il aurait laissé passer. */
  if (cause instanceof ApiError && cause.message === "EMOJI_NOT_ALLOWED")
    return new SignUpError("emoji");
  return new SignUpError("unavailable");
}

/**
 * Crée le compte et OUVRE LA SESSION sur le jeton rendu, comme `signIn` : la suite
 * (retour à la page visée) est celle d'une connexion.
 *
 * ⚠️ AVEC LA LANGUE CHOISIE AU SÉLECTEUR, si la personne en a choisi une (bug #331) :
 * avant la session, ce choix ne vit que dans ce navigateur, et un compte né sans langue
 * recevait ses emails dans la langue de référence. Celle du navigateur, que personne n'a
 * choisie, n'est pas envoyée. Voir `languageToAdopt`.
 */
export async function createAccount(
  domainId: number,
  eventId: number,
  account: NewAccount,
): Promise<void> {
  const languageId = readLanguage(globalThis.localStorage);
  let answer: { token?: string | null } | null;
  try {
    answer = await apiFetch<{ token?: string | null } | null>(
      `/domains/${domainId}/users?eventId=${eventId}`,
      {
        method: "POST",
        body: {
          email: normalizeEmail(account.email),
          firstName: account.firstName.trim(),
          lastName: account.lastName.trim(),
          ...(account.typoId ? { typoId: account.typoId } : {}),
          ...(languageId ? { languageId } : {}),
        },
      },
    );
  } catch (cause) {
    throw signUpRefusal(cause);
  }
  /* Un 200 sans jeton laisserait quelqu'un « connecté » sans rien pour le prouver. */
  if (!answer?.token) throw new SignUpError("unavailable");
  openSession(answer.token);
}
